import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { obtenerListaPersona } from "../api/api-persona";

const parseJsonArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim().length > 0) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (err) {
            return [];
        }
    }
    return [];
};

const extraerDescripciones = (raw) =>
    parseJsonArray(raw)
        .map((item) => item?.descripcion)
        .filter((v) => typeof v === "string" && v.trim().length > 0);

const construirDestinos = (personas, seleccionadosIds) => {
    const set = new Set(seleccionadosIds.map((id) => String(id)));
    const correos = [];
    const telefonos = [];
    personas.forEach((p) => {
        if (!set.has(String(p.id))) return;
        extraerDescripciones(p.correos).forEach((c) => correos.push(c));
        extraerDescripciones(p.telefonos).forEach((tel) => telefonos.push(tel));
    });
    return {
        correos: Array.from(new Set(correos)),
        telefonos: Array.from(new Set(telefonos)),
    };
};

export default function ModalSeleccionarPersonasAlerta({
    isOpen,
    onCancel,
    onConfirmar,
    onSinDestinatario,
}) {
    const { t } = useTranslation();
    const [personas, setPersonas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [seleccionadosIds, setSeleccionadosIds] = useState([]);
    const [busqueda, setBusqueda] = useState("");

    useEffect(() => {
        if (!isOpen) return;
        let activo = true;
        const cargar = async () => {
            setCargando(true);
            try {
                const data = await obtenerListaPersona({ habilitado: 1 }, false);
                if (activo) {
                    setPersonas(Array.isArray(data) ? data : []);
                }
            } finally {
                if (activo) setCargando(false);
            }
        };
        cargar();
        return () => {
            activo = false;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setSeleccionadosIds([]);
            setBusqueda("");
        }
    }, [isOpen]);

    const personasFiltradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        if (q.length === 0) return personas;
        return personas.filter((p) =>
            String(p?.descripcion ?? "").toLowerCase().includes(q)
            || String(p?.codigo ?? "").toLowerCase().includes(q)
        );
    }, [personas, busqueda]);

    const toggleSeleccion = (id) => {
        setSeleccionadosIds((prev) => {
            const idStr = String(id);
            return prev.some((v) => String(v) === idStr)
                ? prev.filter((v) => String(v) !== idStr)
                : [...prev, id];
        });
    };

    if (!isOpen) return null;

    const handleConfirmar = () => {
        const destinos = construirDestinos(personas, seleccionadosIds);
        onConfirmar?.(destinos);
    };

    const handleSinDestinatario = () => {
        onSinDestinatario?.({ correos: [], telefonos: [] });
    };

    return (
        <div className="fixed z-50 top-0 left-0 right-0 bottom-0 flex items-center justify-center">
            <div className="fixed inset-0 bg-gray-500 opacity-75"></div>
            <div className="bg-white w-11/12 md:max-w-lg mx-auto rounded shadow-lg z-50 overflow-hidden flex flex-col max-h-[85vh]">
                <div className="px-6 py-4 border-b border-gray-200">
                    <p className="text-lg font-semibold text-gray-900">
                        {t("label_seleccionar_personas_alerta") || "Seleccionar personas a notificar"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {t("label_seleccionar_personas_alerta_desc") ||
                            "Se enviarán las alertas a los correos y teléfonos de las personas seleccionadas."}
                    </p>
                </div>
                <div className="px-6 py-3 border-b border-gray-200">
                    <input
                        type="text"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder={t("label_buscar") || "Buscar"}
                        className="w-full text-sm px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div className="px-6 py-3 overflow-y-auto flex-1">
                    {cargando ? (
                        <p className="text-sm text-gray-500 py-4 text-center">
                            {t("label_cargando") || "Cargando..."}
                        </p>
                    ) : personasFiltradas.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4 text-center">
                            {t("label_sin_datos") || "Sin datos"}
                        </p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {personasFiltradas.map((p) => {
                                const correos = extraerDescripciones(p.correos);
                                const telefonos = extraerDescripciones(p.telefonos);
                                const checked = seleccionadosIds.some(
                                    (v) => String(v) === String(p.id)
                                );
                                return (
                                    <li key={p.id} className="py-2">
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleSeleccion(p.id)}
                                                className="mt-1"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-gray-900 break-words">
                                                    {p?.descripcion ?? "—"}
                                                </p>
                                                {correos.length > 0 && (
                                                    <p className="text-xs text-gray-500 break-words">
                                                        {correos.join(", ")}
                                                    </p>
                                                )}
                                                {telefonos.length > 0 && (
                                                    <p className="text-xs text-gray-500 break-words">
                                                        {telefonos.join(", ")}
                                                    </p>
                                                )}
                                            </div>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                <div className="px-6 py-3 border-t border-gray-200 flex flex-wrap justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        {t("label_cancelar") || "Cancelar"}
                    </button>
                    <button
                        type="button"
                        onClick={handleSinDestinatario}
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        {t("label_notificar_sin_destinatario") || "Notificar sin destinatario"}
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirmar}
                        disabled={seleccionadosIds.length === 0}
                        className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md ${seleccionadosIds.length === 0
                            ? "bg-blue-300 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"}`}
                    >
                        {t("label_confirmar") || "Confirmar"}
                    </button>
                </div>
            </div>
        </div>
    );
}
